/**
 * LargeFormatJobs — multi-job ordering pattern for retractables/banners.
 *
 *   Job Name      [PO# or label______]   Qty [ 1 ]   $107.23
 *   Job Name      [______________  ]    Qty [ 1 ]   $107.23
 *                                                    ─────────
 *   + Add Another Job   One Artwork Per Job (?)     $214.46
 *
 * Each job is its own cart line — separate Job Name (PO#) + qty + artwork
 * upload. Total price scales linearly: unit_price × sum(qtys). On
 * "Add to Cart" we push one CartItem per row.
 */
import { useMemo, useState } from "react";
import { Plus, Trash2, HelpCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Minus, Plus as PlusIcon } from "lucide-react";
import ArtworkUploader from "./ArtworkUploader";

const emptyJob = () => ({
  job_id: `job_${Math.random().toString(36).slice(2, 9)}`,
  job_name: "",
  quantity: 1,
  artwork: null, // { file_id, original_filename, source_filename }
  artwork_approved: false,
});

export default function LargeFormatJobs({
  user,
  product,
  configHeightIn,
  configWidthIn,
  configShape,
  onJobsChange,
}) {
  const [jobs, setJobs] = useState([emptyJob()]);

  // Bubble jobs upward whenever they change.
  const updateJobs = (next) => {
    setJobs(next);
    onJobsChange?.(next);
  };

  const updateJob = (idx, patch) => {
    const next = jobs.map((j, i) => (i === idx ? { ...j, ...patch } : j));
    updateJobs(next);
  };

  const addJob = () => updateJobs([...jobs, emptyJob()]);
  const removeJob = (idx) => updateJobs(jobs.filter((_, i) => i !== idx));

  const totalQty = useMemo(() => jobs.reduce((s, j) => s + (parseInt(j.quantity) || 0), 0), [jobs]);

  return (
    <div className="space-y-3" data-testid="large-format-jobs">
      <div className="overline">Jobs</div>

      {jobs.map((job, idx) => (
        <JobRow
          key={job.job_id}
          job={job}
          user={user}
          product={product}
          configHeightIn={configHeightIn}
          configWidthIn={configWidthIn}
          configShape={configShape}
          showRemove={jobs.length > 1}
          onChange={(patch) => updateJob(idx, patch)}
          onRemove={() => removeJob(idx)}
          index={idx}
        />
      ))}

      <div className="flex items-center justify-between border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={addJob}
          className="font-mono text-[11px] tracking-[0.22em] uppercase font-bold text-kolt-orange hover:text-kolt-orange-hover flex items-center gap-1.5 transition-colors"
          data-testid="add-job-btn"
        >
          <Plus className="w-4 h-4" /> Add Another Job
          <span className="ml-2 text-slate-400 text-[10px] flex items-center gap-1 normal-case tracking-normal">
            One Artwork Per Job
            <HelpCircle className="w-3 h-3" />
          </span>
        </button>
        <div className="text-right">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Total Units</div>
          <div className="font-display font-black text-lg text-kolt-slate-dark" data-testid="multi-job-total-qty">
            {totalQty.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}

function JobRow({
  job,
  user,
  product,
  configHeightIn,
  configWidthIn,
  configShape,
  showRemove,
  onChange,
  onRemove,
  index,
}) {
  const qty = Math.max(1, parseInt(job.quantity) || 1);

  return (
    <div className="border border-slate-200 bg-white p-3" data-testid={`job-row-${index}`}>
      <div className="grid grid-cols-12 gap-3 items-end">
        <label className="col-span-12 sm:col-span-8">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 font-bold block mb-1.5">
            Job Name
          </span>
          <Input
            value={job.job_name}
            onChange={(e) => onChange({ job_name: e.target.value })}
            placeholder="Job Name / PO# (Required)"
            className="rounded-none h-10"
            data-testid={`job-name-${index}`}
          />
        </label>
        <div className="col-span-9 sm:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 font-bold mb-1.5">Qty</div>
          <div className="inline-flex items-stretch border border-slate-300">
            <button
              type="button"
              onClick={() => onChange({ quantity: Math.max(1, qty - 1) })}
              disabled={qty <= 1}
              className="px-2 h-10 hover:bg-kolt-slate hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-current border-r border-slate-300"
              data-testid={`job-qty-minus-${index}`}
              aria-label="Decrease quantity"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => onChange({ quantity: Math.max(1, parseInt(e.target.value) || 1) })}
              className="w-14 text-center font-mono font-bold text-base bg-white outline-none"
              data-testid={`job-qty-input-${index}`}
            />
            <button
              type="button"
              onClick={() => onChange({ quantity: qty + 1 })}
              className="px-2 h-10 hover:bg-kolt-orange hover:text-white transition-colors border-l border-slate-300"
              data-testid={`job-qty-plus-${index}`}
              aria-label="Increase quantity"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="col-span-3 sm:col-span-1 text-right">
          {showRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-2 hover:bg-red-100 transition-colors"
              data-testid={`job-remove-${index}`}
              aria-label="Remove job"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      </div>

      {/* Artwork upload — one per job */}
      <ArtworkUploader
        user={user}
        productSlug={product.slug}
        productCustomDimensions={product.custom_dimensions}
        configHeightIn={configHeightIn}
        configWidthIn={configWidthIn}
        configShape={configShape}
        uploadedFile={job.artwork}
        onUploaded={(f) => onChange({ artwork: f, artwork_approved: false })}
        onCleared={() => onChange({ artwork: null, artwork_approved: false })}
        onApproved={() => onChange({ artwork_approved: true })}
      />
    </div>
  );
}
